// The toast queue, pure so it is tested. The store and viewport live in hooks/useToast.tsx.

export type ToastVariant = 'info' | 'success' | 'problem';

export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface Toast {
    id: number;
    variant: ToastVariant;
    message: string;
    action?: ToastAction;
    /** Stays until dismissed (problems with an action, e.g. Retry). */
    sticky: boolean;
    /** ms; ignored when sticky. */
    duration: number;
}

export const MAX_TOASTS = 3;
export const DEFAULT_DURATION = 4000;
export const UNDO_DURATION = 6000;

let nextId = 1;

export interface ToastInput {
    variant?: ToastVariant;
    message: string;
    action?: ToastAction;
    sticky?: boolean;
    duration?: number;
}

export function makeToast(input: ToastInput): Toast {
    const variant = input.variant ?? 'info';
    return {
        id: nextId++,
        variant,
        message: input.message,
        action: input.action,
        sticky: input.sticky ?? (variant === 'problem' && input.action !== undefined),
        duration: input.duration ?? (input.action ? UNDO_DURATION : DEFAULT_DURATION)
    };
}

/** Newest last; the oldest non-sticky toast is dropped past the cap, then the oldest of all. */
export function pushToast(queue: Toast[], toast: Toast, max = MAX_TOASTS): Toast[] {
    const next = [...queue, toast];
    while (next.length > max) {
        const victim = next.findIndex(t => !t.sticky && t.id !== toast.id);
        next.splice(victim >= 0 ? victim : 0, 1);
    }
    return next;
}

export const dismissToast = (queue: Toast[], id: number): Toast[] => queue.filter(t => t.id !== id);
